import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { MarkdownEditor } from 'components/blocks/markdown-editor';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Textarea } from 'components/ui/textarea';
import { useAddQnA, useGetFolderList } from 'features/chat/hooks/use-kb';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';

const QUESTION_MIN_LENGTH = 5;
const QUESTION_MAX_LENGTH = 500;
const ANSWER_MIN_LENGTH = 100;
const ANSWER_MAX_LENGTH = 10000;

const createQnaKbFormSchema = (t: (key: string, options?: Record<string, unknown>) => string) =>
  z.object({
    question: z
      .string()
      .trim()
      .max(QUESTION_MAX_LENGTH, t('QUESTION_MAX_CHARACTERS', { max: QUESTION_MAX_LENGTH }))
      .refine((val) => val.replace(/\s+/g, ' ').trim().length >= QUESTION_MIN_LENGTH, {
        message: t('QUESTION_MIN_CHARACTERS_EXCLUDING_WHITESPACE', { min: QUESTION_MIN_LENGTH }),
      }),
    answer: z
      .string()
      .trim()
      .max(ANSWER_MAX_LENGTH, t('ANSWER_MAX_CHARACTERS', { max: ANSWER_MAX_LENGTH }))
      .refine((val) => val.replace(/\s+/g, ' ').trim().length >= ANSWER_MIN_LENGTH, {
        message: t('ANSWER_MIN_CHARACTERS_EXCLUDING_WHITESPACE', { min: ANSWER_MIN_LENGTH }),
      }),
  });

type QnaKbFormValues = z.infer<ReturnType<typeof createQnaKbFormSchema>>;

const qnaKbFormDefaultValues: QnaKbFormValues = {
  question: '',
  answer: '',
};

type AddKbQaModalProps = {
  folderId: string;
  projectKey: string;
};

export function AddKbQaModal({ folderId, projectKey }: AddKbQaModalProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const qnaKbFormSchema = useMemo(() => createQnaKbFormSchema(t), [t]);
  const form = useForm<QnaKbFormValues>({
    defaultValues: qnaKbFormDefaultValues,
    resolver: zodResolver(qnaKbFormSchema),
    mode: 'onChange',
  });

  const validateAnswer = useMemo(
    () => (value: string): string | undefined => {
      const result = qnaKbFormSchema.shape.answer.safeParse(value);
      if (!result.success) {
        return result.error.errors[0].message;
      }
      return undefined;
    },
    [qnaKbFormSchema]
  );

  const { isValid } = form.formState;
  const questionValue = form.watch('question') || '';
  const answerValue = form.watch('answer') || '';
  const isQuestionValid =
    questionValue.replace(/\s+/g, ' ').trim().length >= QUESTION_MIN_LENGTH;
  const isAnswerValid =
    answerValue.replace(/\s+/g, ' ').trim().length >= ANSWER_MIN_LENGTH;
  const isFormValid = isValid && isQuestionValid && isAnswerValid;
  const { mutateAsync: addQnAMutate, isPending } = useAddQnA();
  const { data: folderListData } = useGetFolderList(projectKey, !!projectKey);
  const folder = useMemo(
    () => folderListData?.data?.folders?.find((f) => f._id === folderId),
    [folderId, folderListData?.data?.folders]
  );
  const canEdit = useIsOwner(folder);

  const resetState = () => {
    form.reset(qnaKbFormDefaultValues);
  };

  const submitHandler = async (values: QnaKbFormValues) => {
    if (!canEdit) return;

    try {
      const response = await addQnAMutate({
        ...values,
        project_key: projectKey,
        folder_id: folderId,
      });

      if (!response.is_success) {
        throw new Error(response.detail || t('FAILED_TO_ADD_QA_TO_KB'));
      }

      toast({
        variant: 'success',
        title: t('QA_ADDED'),
        description: t('QA_PAIR_ADDED_TO_FOLDER'),
      });
      resetState();
      setOpen(false);
    } catch (error) {
      toast({
        title: t('UNABLE_TO_ADD_QA'),
        description: error instanceof Error ? error.message : t('PLEASE_TRY_AGAIN'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen && !canEdit) return;
        setOpen(nextOpen);
        if (!nextOpen) {
          resetState();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" disabled={!canEdit}>
          <Plus className="h-4 w-4" />
          {t('ADD_QA')}
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] max-w-lg flex-col overflow-hidden p-5 md:min-w-[720px] md:max-w-[720px]">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('ADD_QA')}</DialogTitle>
          <DialogDescription>{t('TRAIN_AI_WITH_QA')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(submitHandler)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="question"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('QUESTION')}</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={2}
                          placeholder={t('QUESTION_PLACEHOLDER')}
                          className="min-h-fit resize-none"
                          maxLength={QUESTION_MAX_LENGTH}
                          {...field}
                        />
                      </FormControl>
                      <div className="flex justify-end text-xs text-muted-foreground">
                        {questionValue.length}/{QUESTION_MAX_LENGTH}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="answer"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('ANSWER')}</FormLabel>
                      <FormControl>
                        <MarkdownEditor
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder={t('ANSWER_PLACEHOLDER')}
                          disabled={isPending}
                          minLength={ANSWER_MIN_LENGTH}
                          maxLength={ANSWER_MAX_LENGTH}
                          showCharacterCount
                          contentClassName="min-h-32 max-h-none"
                          error={fieldState.error?.message}
                          validate={validateAnswer}
                          showToolbar
                          showModeToggle
                          defaultMode="edit"
                          isInvalid={!!fieldState.error}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter className="mt-4 shrink-0 flex flex-row justify-start gap-2 border-t border-border pt-4">
              <Button
                type="submit"
                size="sm"
                className="flex-1 sm:flex-none sm:w-20"
                disabled={isPending || !isFormValid}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('ADDING')}
                  </>
                ) : (
                  t('ADD')
                )}
              </Button>
              <DialogClose asChild>
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none sm:w-20">
                  {t('CANCEL')}
                </Button>
              </DialogClose>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
